


import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Grid,
  InputAdornment,
  TextField,
  Button,
  CircularProgress,
    ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { Building2, Home, Trees, BedDouble, Warehouse, Tent } from 'lucide-react';
import HotelIcon from '@mui/icons-material/Hotel';
import HomeIcon from '@mui/icons-material/Home';
import VillaIcon from '@mui/icons-material/Villa';
import ApartmentIcon from '@mui/icons-material/Apartment';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import GrassIcon from '@mui/icons-material/Grass';
import BedIcon from '@mui/icons-material/Bed';
import GroupsIcon from '@mui/icons-material/Groups';
import { Drawer, Fab } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Search, MapPin, Calendar, Users } from 'lucide-react';
import { PropertyCard } from './PropertyCard';
import { PropertyDetail } from './PropertyDetail';
import { BookingModal } from './BookingModal';
import { FilterSidebar } from './FilterSidebar';
import { HeroSection } from './HeroSection';
import { KeyUri } from '../key';
import { fetchUserTerms, termsSelector } from '../api/termsSlice';
import { useNavigate } from "react-router-dom";
import CloseIcon from '@mui/icons-material/Close';

const PROPERTY_TYPE_OPTIONS = [
  { label: 'Hotel', value: 'hotel' },
  { label: 'Homestay', value: 'homestay' },
  { label: 'Resort', value: 'resort' },
  { label: 'Villa', value: 'villa' },
  { label: 'Farmstay', value: 'farmstay' },
  { label: 'Guest House', value: 'guesthouse' },
  { label: 'Cottage', value: 'cottage' },
  { label: 'Apartment', value: 'apartment' },
   { label: 'Hostel', value: 'hostel' },
  { label: 'Dormitory', value: 'dormitory' },
];

const getTypeIcon = (type) => {
  switch (type) {
    case 'hotel':
      return <HotelIcon fontSize="small" />;

    case 'homestay':
      return <HomeIcon fontSize="small" />;

    case 'resort':
      return <HolidayVillageIcon fontSize="small" />;

    case 'villa':
      return <VillaIcon fontSize="small" />;

    case 'farmstay':
      return <GrassIcon fontSize="small" />;

    case 'apartment':
      return <ApartmentIcon fontSize="small" />;

    case 'cottage':
      return <HomeIcon fontSize="small" />;

    case 'hostel':
      return <BedIcon fontSize="small" />;

    case 'dormitory':
      return <GroupsIcon fontSize="small" />;

    default:
      return <HomeIcon fontSize="small" />;
  }
};

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfWeek = (year, month) => new Date(year, month, 1).getDay();

const formatDisplayDate = (date) => {
  if (!date) return null;
  return {
    dateStr: `${date.getDate()} ${MONTH_NAMES[date.getMonth()].slice(0, 3)}, ${date.getFullYear()}`,
    weekday: date.toLocaleDateString('en-US', { weekday: 'long' }),
  };
};

const CalendarPopover = ({ selectedDate, viewMonth, viewYear, onPrevMonth, onNextMonth, onSelect, minDate }) => {
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfWeek(viewYear, viewMonth);
  const floor = minDate ? new Date(minDate) : new Date();
  floor.setHours(0, 0, 0, 0);
  const todayFloor = new Date();
  todayFloor.setHours(0, 0, 0, 0);

  const cells = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <div className="w-64 bg-white rounded-lg shadow-2xl border border-slate-100 p-2.5" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={onPrevMonth} className="w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">
          ‹
        </button>
        <span className="text-xs font-semibold text-slate-800">{MONTH_NAMES[viewMonth]} {viewYear}</span>
        <button type="button" onClick={onNextMonth} className="w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-0.5">
        {DAY_NAMES.map((d) => (
          <div key={d} className="text-center text-[10px] font-semibold text-slate-400 py-0.5">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (d === null) return <div key={`e-${i}`} />;
          const cellDate = new Date(viewYear, viewMonth, d);
          cellDate.setHours(0, 0, 0, 0);
          const isPast = cellDate < floor;
          const isToday = cellDate.getTime() === todayFloor.getTime();
          const isSelected =
            selectedDate &&
            selectedDate.getFullYear() === viewYear &&
            selectedDate.getMonth() === viewMonth &&
            selectedDate.getDate() === d;

          return (
            <button
              key={d}
              type="button"
              disabled={isPast}
              onClick={() => onSelect(cellDate)}
              className={`h-7 rounded-md text-xs font-medium transition-colors
                ${isPast ? 'text-slate-300 cursor-not-allowed' : 'text-slate-700 hover:bg-blue-50'}
                ${isSelected ? '!bg-blue-600 !text-white' : ''}
                ${isToday && !isSelected ? 'border border-blue-400' : ''}`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default function App() {
  const location = useLocation();
  
const navigate = useNavigate();
 const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  //const [selectedPropertyId, setSelectedPropertyId] = useState(null);

  // Reset to property list when navigating to /hotels
 
  //const [selectedPropertyObj, setSelectedPropertyObj] = useState(null);
 // const [loadingDetail, setLoadingDetail] = useState(false);

  const [showBookingModal, setShowBookingModal] = useState(false);
const [searchParams, setSearchParams] = useSearchParams();

const [searchLocation, setSearchLocation] = useState(
  searchParams.get('location') || ''
);

const [filters, setFilters] = useState({
  propertyTypes: searchParams.get('propertyTypes')
    ? searchParams.get('propertyTypes').split(',')
    : [],
  priceRange: [
    Number(searchParams.get('minPrice')) || 0,
    Number(searchParams.get('maxPrice')) || 50000,
  ],
  amenities: searchParams.get('amenities')
    ? searchParams.get('amenities').split(',')
    : [],
  ratings: Number(searchParams.get('ratings')) || 0,
});

const [checkIn, setCheckIn] = useState("");
const [checkOut, setCheckOut] = useState("");
const [checkInOpen, setCheckInOpen] = useState(false);
const [checkOutOpen, setCheckOutOpen] = useState(false);
const [checkInViewMonth, setCheckInViewMonth] = useState(new Date().getMonth());
const [checkInViewYear, setCheckInViewYear] = useState(new Date().getFullYear());
const [checkOutViewMonth, setCheckOutViewMonth] = useState(new Date().getMonth());
const [checkOutViewYear, setCheckOutViewYear] = useState(new Date().getFullYear());

  const [showFilters, setShowFilters] = useState(false);
const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
 // console.log('sample property types:', properties.map(p => p.propertyType));
}, [properties]);

useEffect(() => {
  const initAutocomplete = () => {
    if (window.google?.maps?.places && inputRef.current && !autocompleteRef.current) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
        
          componentRestrictions: { country: 'in' }, // India only
          
        }
      );

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (!place.formatted_address) return;
        
        setSearchLocation(place.formatted_address);
        //console.log('Selected:', place.formatted_address);
      });
    }
  };

  const interval = setInterval(initAutocomplete, 100);
  return () => clearInterval(interval);
}, []);


  // Fetch properties from backend API
  useEffect(() => {
    async function getProperties() {
      setLoading(true);
      try {
      
        const res = await axios.get(KeyUri.BACKEND_URI + '/properties-user');
         
        setProperties(res.data.data);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to fetch properties');
      } finally {
        setLoading(false);
      }
    }
    getProperties();
  }, []);

useEffect(() => {
  const params = {};

  if (searchLocation?.trim()) params.location = searchLocation.trim();
  if (filters.propertyTypes.length > 0) params.propertyTypes = filters.propertyTypes.join(',');
  if (filters.priceRange[0] > 0) params.minPrice = filters.priceRange[0];
  if (filters.priceRange[1] < 50000) params.maxPrice = filters.priceRange[1];
  if (filters.amenities.length > 0) params.amenities = filters.amenities.join(',');
  if (filters.ratings > 0) params.ratings = filters.ratings;

  setSearchParams(params, { replace: true });
}, [searchLocation, filters, setSearchParams]);

/*const filteredProperties = properties.filter((property) => {
  if (
    searchLocation &&
    !property.location?.city?.toLowerCase().includes(searchLocation.toLowerCase()) &&
    !property.location?.state?.toLowerCase().includes(searchLocation.toLowerCase())
  )
    return false;

  if (
    filters.propertyTypes.length > 0 &&
    !filters.propertyTypes.includes(property.propertyType)
  )
    return false;

  const accommodations = property.accommodations || [];
  const minPrice = accommodations.length
    ? Math.min(...accommodations.map((acc) => acc.pricing.basePrice))
    : Infinity;

  if (minPrice < filters.priceRange[0] || minPrice > filters.priceRange[1])
    return false;

  // Flatten amenities object to a string array
  const amenitiesObj = property.amenities || {};
  const allAmenities = [
    ...(amenitiesObj.general || []),
    ...(amenitiesObj.recreational || []),
    ...(amenitiesObj.dining || []),
    ...(amenitiesObj.services || []),
  ];
  const allAmenitiesLower = allAmenities.map(a => a.toLowerCase());

  if (
    filters.amenities.length > 0 &&
    !filters.amenities.every(amenity =>
      allAmenitiesLower.includes(amenity.toLowerCase())
    )
  )
    return false;

  if (filters.ratings > 0 && property.rating < filters.ratings) return false;

  return true;
});*/

const handlePropertyTypeChange = (_event, newTypes) => {
    setFilters((prev) => ({ ...prev, propertyTypes: newTypes }));
  };

 /*const filteredProperties = useMemo(() => {
    if (!searchLocation) return properties;
    
    const searchLower = searchLocation.toLowerCase();
    return properties.filter(property => 
      property.location?.city?.toLowerCase().includes(searchLower) ||
      property.location?.state?.toLowerCase().includes(searchLower) ||
      property.propertyName?.toLowerCase().includes(searchLower)
    );
  }, [properties, searchLocation]);*/
const filteredProperties = useMemo(() => {
  return properties.filter((property) => {
   if (searchLocation) {
  const searchLower = searchLocation.toLowerCase();
  const searchCity = searchLower.split(',')[0].trim();

  const propertyCity = (property.location?.city || '').toLowerCase();
  const propertyState = (property.location?.state || '').toLowerCase();

 
  if (propertyCity) {
    if (!propertyCity.includes(searchCity)) {
      return false;
    }
  } 
 
  else if (!propertyState.includes(searchCity)) {
    return false;
  }
}


   
    if (filters.propertyTypes.length > 0 && !filters.propertyTypes.includes(property.propertyType)) {
      return false;
    }

const accommodations = property.accommodations || [];
const prices = accommodations
  .map((acc) => acc.pricePerNightB2C || 0)  
  .filter((p) => p > 0);  
const minPrice = prices.length ? Math.min(...prices) : null;

if (minPrice !== null && (minPrice < filters.priceRange[0] || minPrice > filters.priceRange[1])) {
  return false;
}


      // Amenities filter
      const amenitiesObj = property.amenities || {};
      const allAmenities = [
        ...(amenitiesObj.general || []),
        ...(amenitiesObj.recreational || []),
        ...(amenitiesObj.dining || []),
        ...(amenitiesObj.services || []),
      ];
      const allAmenitiesLower = allAmenities.map((a) => a.toLowerCase());

      if (
        filters.amenities.length > 0 &&
        !filters.amenities.every((amenity) =>
          allAmenitiesLower.includes(amenity.toLowerCase())
        )
      ) {
        return false;
      }

      // Ratings filter
      if (filters.ratings > 0 && property.rating < filters.ratings) {
        return false;
      }

      return true;
    });
  }, [properties, searchLocation, filters]);



  
  const handleBookNow = () => {
    setShowBookingModal(true);
  };

  //const selectedPropertyObj = selectedProperty
  //  ? properties.find((p) => p._id === selectedProperty)
  //  : null;

  if (loading)
    return (
      <Box textAlign="center" mt={4}>
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Typography color="error" mt={4} align="center">
        {error}
      </Typography>
    );

const handlePropertySelect = (id) => {
  navigate(`/hotel/property/${id}`);
};

const today = new Date().toISOString().split("T")[0];
 const checkInDateObj = checkIn ? new Date(checkIn + 'T00:00:00') : null;
 const checkOutDateObj = checkOut ? new Date(checkOut + 'T00:00:00') : null;
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <HeroSection />

    
 

      {/* Search Bar */}
      <Box
        maxWidth="xl"
        mx="auto"
        px={{ xs: 1, sm: 3, lg: 4 }}
        mt={-2}
        position="relative"
        zIndex={10}
      >
        <Box
          sx={{ bgcolor: 'background.paper', borderRadius: 4, p: 3, boxShadow: 6 }}
        >
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
              inputRef={inputRef}
                fullWidth
                placeholder="Where to?"
                label="Location"
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
                 inputProps={{
    min: today, 
  }}               
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MapPin size={20} color="#9ca3af" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
    <Grid item xs={12} md={3}>
  <Box sx={{ position: 'relative' }}>
    <Box
      onClick={() => { setCheckInOpen((o) => !o); setCheckOutOpen(false); }}
      sx={{
        border: '1px solid rgba(0,0,0,0.23)',
        borderRadius: 1,
        px: 1.75,
        py: 1.9,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        '&:hover': { borderColor: 'rgba(0,0,0,0.87)' },
      }}
    >
      <Calendar size={20} color="#9ca3af" />
      <Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1 }}>
          Check-in
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: checkInDateObj ? 'text.primary' : 'text.disabled' }}>
          {checkInDateObj ? formatDisplayDate(checkInDateObj).dateStr : 'Add date'}
        </Typography>
      </Box>
    </Box>

    {checkInOpen && (
      <>
        <Box onClick={() => setCheckInOpen(false)} sx={{ position: 'fixed', inset: 0, zIndex: 40 }} />
        <Box sx={{ position: 'absolute', zIndex: 50, top: '100%', left: 0, mt: 1 }}>
          <CalendarPopover
            selectedDate={checkInDateObj}
            viewMonth={checkInViewMonth}
            viewYear={checkInViewYear}
            onPrevMonth={() => {
              if (checkInViewMonth === 0) { setCheckInViewMonth(11); setCheckInViewYear((y) => y - 1); }
              else setCheckInViewMonth((m) => m - 1);
            }}
            onNextMonth={() => {
              if (checkInViewMonth === 11) { setCheckInViewMonth(0); setCheckInViewYear((y) => y + 1); }
              else setCheckInViewMonth((m) => m + 1);
            }}
            onSelect={(date) => {
              const iso = date.toISOString().slice(0, 10);
              setCheckIn(iso);
              if (checkOut && iso > checkOut) setCheckOut("");
              setCheckInOpen(false);
            }}
          />
        </Box>
      </>
    )}
  </Box>
</Grid>
       <Grid item xs={12} md={3}>
  <Box sx={{ position: 'relative' }}>
    <Box
      onClick={() => { setCheckOutOpen((o) => !o); setCheckInOpen(false); }}
      sx={{
        border: '1px solid rgba(0,0,0,0.23)',
        borderRadius: 1,
        px: 1.75,
        py: 1.9,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        '&:hover': { borderColor: 'rgba(0,0,0,0.87)' },
      }}
    >
      <Calendar size={20} color="#9ca3af" />
      <Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1 }}>
          Check-out
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: checkOutDateObj ? 'text.primary' : 'text.disabled' }}>
          {checkOutDateObj ? formatDisplayDate(checkOutDateObj).dateStr : 'Add date'}
        </Typography>
      </Box>
    </Box>

    {checkOutOpen && (
      <>
        <Box onClick={() => setCheckOutOpen(false)} sx={{ position: 'fixed', inset: 0, zIndex: 40 }} />
        <Box sx={{ position: 'absolute', zIndex: 50, top: '100%', left: 0, mt: 1 }}>
          <CalendarPopover
            selectedDate={checkOutDateObj}
            viewMonth={checkOutViewMonth}
            viewYear={checkOutViewYear}
            minDate={checkIn || today}
            onPrevMonth={() => {
              if (checkOutViewMonth === 0) { setCheckOutViewMonth(11); setCheckOutViewYear((y) => y - 1); }
              else setCheckOutViewMonth((m) => m - 1);
            }}
            onNextMonth={() => {
              if (checkOutViewMonth === 11) { setCheckOutViewMonth(0); setCheckOutViewYear((y) => y + 1); }
              else setCheckOutViewMonth((m) => m + 1);
            }}
            onSelect={(date) => {
              setCheckOut(date.toISOString().slice(0, 10));
              setCheckOutOpen(false);
            }}
          />
        </Box>
      </>
    )}
  </Box>
</Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Guests"
                type="number"
                inputProps={{ min: 1 }}
                placeholder="2 guests"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Users size={20} color="#9ca3af" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
      <Box
  sx={{
    mt: 2,
    mb: 1,
    display: 'flex',
    overflowX: 'auto',
  }}
>
  <ToggleButtonGroup
    value={filters.propertyTypes}
    onChange={handlePropertyTypeChange}
    exclusive={false}
    aria-label="property types"
    sx={{
      '& .MuiToggleButton-root': {
        textTransform: 'none',
        borderRadius: 999,
        border: '1px solid #e5e7eb',
        px: { xs: 1.5, sm: 2.5 },         
        py: { xs: 0.5, sm: 0.75 },
        mx: 0.75,
        fontSize: { xs: '0.7rem', sm: '0.85rem' },
        color: '#374151',
        backgroundColor: '#ffffff',
        '& svg': { marginRight: 0.5,
          width: { xs: 14, sm: 18 },       
        height: { xs: 14, sm: 18 },
         },
        '&:hover': {
          borderColor: '#cbd5f5',
          backgroundColor: '#f9fafb',
        },
        '&.Mui-selected': {
          backgroundColor: '#eff6ff',
          borderColor: '#3b82f6',
          color: '#1d4ed8',
          '&:hover': {
            backgroundColor: '#dbeafe',
          },
        },
      },
      '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
        marginLeft: 0,
      },
      '& .MuiToggleButtonGroup-grouped': {
        border: '1px solid #e5e7eb',
      },
    }}
  >
   {PROPERTY_TYPE_OPTIONS.map((item) => (
  <ToggleButton key={item.value} value={item.value}>
    {getTypeIcon(item.value)}
    {item.label}
  </ToggleButton>
))}
  </ToggleButtonGroup>
</Box>


          <Box mt={3} display="flex" gap={2}>
            <Button
              fullWidth
              size="large"
              startIcon={<Search size={20} />}
              variant="contained"
            >
              Search
            </Button>
            {/* <Button
              fullWidth
              size="large"
              variant="outlined"
              onClick={() => setShowFilters((prev) => !prev)}
            >
              Filters
            </Button> */}
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box maxWidth="xl" mx="auto" px={{ xs: 1, sm: 3, lg: 4 }} py={{ xs: 3, md: 6 }}>
        <Box
  sx={{
    display: 'flex',
    gap: 4,
    alignItems: 'flex-start',
  }}
>

       {/* Sidebar */}
<Box
  sx={{
    width: 280,
    flexShrink: 0,
    display: { xs: 'none', md: 'block' },
    position: 'sticky',
    top: 120,
    maxHeight: 'calc(100vh - 140px)',  
    overflowY: 'auto',                
    pr: 1,
     '&::-webkit-scrollbar': { display: 'none' },
    '& *::-webkit-scrollbar': { display: 'none' },
    msOverflowStyle: 'none', 
    scrollbarWidth: 'none', 
  }}
>
  <FilterSidebar
    filters={filters}
    onFilterChange={setFilters}
  />
</Box>



          <Box flex={1} sx={{ width: '100%', overflowX: 'hidden' }}>
            <Typography variant="h4" mb={1}>
              {filteredProperties.length} properties found
              {searchLocation ? ` in ${searchLocation}` : ''}
            </Typography>
            <Typography color="text.secondary" mb={4}>
              Best deals on hotels, homestays, resorts and more
            </Typography>

            <Grid container spacing={2}>
              {filteredProperties.length > 0 ? (
                filteredProperties.map((property) => (
                  <Grid item xs={12} sm={6} md={4} key={property._id}>
                    <PropertyCard property={property} onSelect={handlePropertySelect} />
                  </Grid>
                ))
              ) : (
                <Box textAlign="center" py={10} width="100%">
                  <Typography variant="h6" mb={1}>
                    No properties found
                  </Typography>
                  <Typography color="text.secondary">Try adjusting your filters or search criteria</Typography>
                </Box>
              )}
            </Grid>
          </Box>
        </Box>
      </Box>
    <Box
  sx={{
    position: 'fixed',
    bottom: 65,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1500,
    display: { xs: 'flex', md: 'none' },
  }}
>
  <Button
    onClick={() => setMobileFilterOpen(prev => !prev)}
    startIcon={<FilterListIcon />}
    sx={{
      px: 3,
      py: 1.2,
      borderRadius: 999,
      backdropFilter: 'blur(10px)',
      background: 'rgba(0,0,0,0.6)',
      color: '#fff',
      textTransform: 'none',
      boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
      '&:hover': {
        background: 'rgba(0,0,0,0.65)',
      },
    }}
  >
    Filters
  </Button>
</Box>

<Drawer
  anchor="bottom"
  open={mobileFilterOpen}
  onClose={() => setMobileFilterOpen(false)}
  PaperProps={{
    sx: {
      height: '90vh',
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      p: 2,
    },
  }}
>
  {/* Header */}
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="center"
    mb={2}
  >
    <Typography variant="h6">Filters</Typography>
    <Button onClick={() => setMobileFilterOpen(false)}>
      <CloseIcon />
    </Button>
  </Box>

  <FilterSidebar
    filters={filters}
    onFilterChange={setFilters}
  />

  {/* Apply Button */}
  <Box
    sx={{
      position: 'sticky',
      bottom: 0,
      bgcolor: 'white',
      pt: 2,
    }}
  >
    <Button
      fullWidth
      variant="contained"
      size="large"
      onClick={() => setMobileFilterOpen(false)}
    >
      Apply Filters
    </Button>
  </Box>
</Drawer>


    </Box>
  );
}
